import React, { useEffect, useState } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import ManageClient from "./ManageClient";
import Pagination from "../../components/pagination/Pagination";

import { getUser } from "../../auth/Auth";
import { loadFavoriteClients, loadStatus } from "../../common/Common";
import { postRequest } from "../../routes/Routes";
import Loading from "../../components/Loading";


import '../../assets/css/ManageAdmins.css';

import avatar from "../../assets/images/avatar.png";
import moreButton from "../../assets/images/more-button.svg";
import favoriteIcon from "../../assets/images/favorite-icon.svg";
import activeFavoriteIcon from "../../assets/images/active-favorite-icon.svg";

function FavoriteClients({ count, updateCount }) {
    const [user, setUser] = useState(null),
        [clients, setClients] = useState([]),
        [filteredClients, setFilteredClients] = useState([]),
        [addClient, setAddClient] = useState(false),
        [selectedClient, setSelectedClient] = useState(null),
        [selectedDate, setSelectedDate] = useState({ start: null, end: null }),
        [selectedClients, setSelectedClients] = useState([]),
        [currentPage, setCurrentPage] = useState(1),
        [isMobile, setIsMobile] = useState(false),
        [filterType, setFilterType] = useState({ statusId: 'All Status Types', status: 'All Status Types' }),
        [filterClientName, setFilterClientName] = useState(null),
        [filterBusinessType, setFilterBusinessType] = useState(null),
        [statusList, setStatusList] = useState([]),
        [loadingContent, setLoadingContent] = useState(true),
        [link] = useState(decodeURI(window.location.href.split('/').at(-1)));

    const [filterClientActiveStatus, setFilterClientActiveStatus] = useState("All");


    const navigate = useNavigate();

    useEffect(() => {
        getUser().then((data) => {
            setUser(data);
            let values = {
                email: data.email,
                context: {
                    action: 'List Favorite Clients'
                },
                endpoint: '/api/user-favorite-client/list',
                token: data.token
            };

            loadFavoriteClients(values).then(data => {
                const uniqueClients = Array.from(new Map(data.map(c => [c.id, c])).values());
                setClients(uniqueClients);
                setFilteredClients(uniqueClients);
                setLoadingContent(false);
            }).catch(() => null);

            values = {
                context: {
                    action: 'List Status'
                },
                endpoint: '/api/status/list',
                token: data.token
            };
            loadStatus(values).then(data => {
                setStatusList(data);
            }).catch(() => null);
        }).catch(() => null);

        if (link === "add-client") {
            setAddClient(true);
        }
    }, []);

    useEffect(() => {
        const updateMedia = () => setIsMobile(window.innerWidth < 768);
        updateMedia();
        window.addEventListener('resize', updateMedia);
        return () => window.removeEventListener('resize', updateMedia);
    }, []);

    useEffect(() => {
        if (user) {
            let values = {
                email: user.email,
                context: {
                    action: 'List Favorite Clients'
                },
                endpoint: '/api/user-favorite-client/list',
                token: user.token
            };
            loadFavoriteClients(values).then(data => {
                setClients(data);
                setFilteredClients(data);
            }).catch(() => null);
        }
    }, [count]);

    const addClientButton = () => {
        setSelectedClients([]); // Clear selected clients when entering edit view
        setAddClient(true);
        navigate("/add-client");
    }

    const toggleClientActiveStatus = (client) => {
        const newActiveId = client.active?.activeId === 1 ? 2 : 1;

        const updatedDto = {
            id: client.id,
            users: client.users,
            status: client.status,
            active: {
                activeId: newActiveId
            }
        };

        const values = {
            dto: updatedDto,
            context: {
                action: 'Update Client'
            },
            endpoint: '/api/client/update',
            token: user.token
        };

        postRequest(values).then(response => {
            if (response.status === 200) {
                toast.success(`Client ${newActiveId === 1 ? 'is active' : 'is inactive'} `, {
                    position: "bottom-center",
                    autoClose: 3000
                });

                // Update client lists
                setClients(prev =>
                    prev.map(c => c.id === client.id ? { ...response.data } : c)
                );
                setFilteredClients(prev =>
                    prev.map(c => c.id === client.id ? { ...response.data } : c)
                );
                updateCount();
            } else if (response.status === 401) {
                toast.error("Session expired. Please log in again.");
                window.location.href = "/login";
            } else {
                toast.error("Failed to update client status.");
            }
        }).catch(err => {
            console.error(err);
            toast.error("Network error while updating client status.");
        });
    };


    const backButton = () => {
        setAddClient(false);
        setSelectedClient(null);
        setSelectedClients([]);
        let values = {
            email: user.email,
            context: {
                action: 'List Favorite Clients'
            },
            endpoint: '/api/user-favorite-client/list',
            token: user.token
        };
        loadFavoriteClients(values).then(data => {
            setClients(data);
            setFilteredClients(data);
        }).catch(() => null);
        updateCount();
        navigate("/favorite-clients");
    }

    const filterClients = (text, filterType, date, businessType, activeStatus = "Active") => {
        let filteredClients;
        if (!text || text === '') {
            filteredClients = clients;
        } else {
            text = text.toLowerCase();
            filteredClients = clients.filter((user) => `${user.name}`.toLowerCase().includes(text) || `${user.email}`.toLowerCase().includes(text));
        }
        if (!(!businessType || businessType === '')) {
            businessType = businessType.toLowerCase();
            filteredClients = filteredClients.filter((user) => `${user.businessType}`.toLowerCase().includes(businessType));
        }
        if (filterType.statusId !== 'All Status Types') {
            filteredClients = filteredClients.filter((user) => user.status.statusId === filterType.statusId);
        }
        if (date.start) {
            filteredClients = filteredClients.filter((user) => new Date(user.lastUpdated) >= date.start);
        }
        if (date.end) {
            filteredClients = filteredClients.filter((user) => new Date(user.lastUpdated) <= date.end);
        }
        if (activeStatus !== "All") {
            filteredClients = filteredClients.filter(user =>
                user.active?.activeStatus?.toLowerCase() === activeStatus.toLowerCase()
            );
        }
        setFilteredClients(filteredClients);
    };

    const handleSelectCheckBox = (client) => {
        if (selectedClients.includes(client)) {
            setSelectedClients(values =>
                values.filter(function (selectedClient) {
                    return selectedClient !== client;
                }),
            );
        } else {
            setSelectedClients(values => [...values, client]);
        }
    };

    const tableBody = (clients) => {
        let body = [];
        if (clients) {
            let status,

                sortedClients = [];
            statusList.forEach(function (key) {
                clients = clients.filter(function (item) {
                    if (item.status.statusId === key.statusId) {
                        sortedClients.push(item);
                        return false;
                    } else {
                        return true;
                    }
                })
            });
            sortedClients.forEach((client, index) => {
                if (index === 0 || status !== client.status.status) {
                    body.push(
                        <tr className="status-row" key={index + "statusKey"}>
                            <td colSpan={6}>
                                <h4>{client.status.status}</h4>
                            </td>
                        </tr>
                    );
                    status = client.status.status;
                }
                body.push(
                    <tr key={index} className={selectedClients.includes(client) ? "blue-background-row" : ""}>
                        <td style={{ width: "10%" }}>
                            <div className="custom-control custom-checkbox">
                                <input type="checkbox" className="custom-control-input"
                                    id={'selectFavoriteClient' + client.id}
                                    checked={selectedClients.includes(client)}
                                    onChange={(event) => handleSelectCheckBox(client)} />
                                <label className="custom-control-label" htmlFor={'selectFavoriteClient' + client.id} />
                            </div>
                        </td>
                        <td style={{ width: "20%" }} className="mobile-client-column">
                            {client.imageUrl ?
                                <img src={client.imageUrl} alt={client.firstName} className='user-image' />
                                : <img src={avatar} alt="Avatar" className='user-image' />
                            }
                            <div>
                                {client.name}
                                <span style={{
                                    color: client.status.fontColor,
                                    backgroundColor: client.status.backgroundColor,
                                    padding: "5% 10%",
                                    borderRadius: 20
                                }} className="mobile-client-status">
                                    {client.status.status}
                                </span>
                            </div>
                        </td>
                        <td style={{ width: "20%" }} className="desktop-client-column">
                            {client.imageUrl ?
                                <img src={client.imageUrl} alt={client.firstName} className='user-image' />
                                : <img src={avatar} alt="Avatar" className='user-image' />
                            }
                            {client.name}
                        </td>
                        <td style={{ width: "20%" }} className="desktop-client-column">
                            <span style={{
                                color: client.status.fontColor,
                                backgroundColor: client.status.backgroundColor,
                                padding: "5% 10%",
                                borderRadius: 20
                            }}>
                                {client.status.status}
                            </span>
                        </td>
                        <td style={{ width: "20%" }} className="desktop-client-column">
                            Financial Date: <strong>{client.financialDate}</strong><br />
                            Deadline Date: <strong>{client.deadlineDate}</strong>
                        </td>
                        <td style={{ width: "20%" }} className="desktop-client-column">
                            <strong>{client.email}</strong><br />
                            {client.primaryUser &&
                                <p>
                                    Primary
                                    User: <strong>{client.primaryUser.firstName + ' ' + client.primaryUser.lastName}</strong><br />
                                    User phone: <strong>{client.primaryUser.phone ? client.primaryUser.phone : ''}</strong>
                                </p>
                            }
                        </td>
                        <td style={{ width: "5%" }}>
                            <div className="dropdown">
                                <button className="borderless-button" type="button"
                                    data-bs-toggle="dropdown" aria-expanded="false">
                                    <img src={moreButton} alt="More Button" />
                                </button>
                                <ul className="dropdown-menu">
                                    <li>
                                        <a className="dropdown-item" onClick={() => {
                                            setAddClient(true);
                                            setSelectedClient(client)
                                        }}>Edit</a>
                                    </li>
                                    <li>
                                        <a className="dropdown-item" onClick={() => {
                                            setSelectedClient(client)
                                        }} data-bs-toggle="modal"
                                            data-bs-target="#updateFavoriteClientStatusModal">
                                            Update Status
                                        </a>
                                    </li>
                                    <li>
                                        <a className="dropdown-item"
                                            onClick={() => toggleClientActiveStatus(client)}>
                                            {client.active?.activeStatus === 'active' ? 'Inactivate' : 'Activate'}
                                        </a>
                                    </li>

                                </ul>
                            </div>
                        </td>
                        <td style={{ width: "5%" }}>
                            <button
                                className={"borderless-button active-favorite-button"}
                                type="button"
                                onClick={() => handleFavoriteClient(client)}>
                                <img src={activeFavoriteIcon} alt="Favorite Icon" className="active-image" />
                            </button>
                        </td>
                    </tr>
                )
            });
        }
        return body;
    }

    const handleFavoriteClient = (client) => {
        let tempFavoriteClients = clients;
        if (clients.some((favoriteClient) => Object.entries(favoriteClient).toString() === Object.entries(client).toString())) {
            setClients(values =>
                values.filter(function (favoriteClient) {
                    return Object.entries(favoriteClient).toString() !== Object.entries(client).toString();
                }),
            );
            tempFavoriteClients = tempFavoriteClients.filter(function (favoriteClient) {
                return Object.entries(favoriteClient).toString() !== Object.entries(client).toString();
            });
        } else {
            setClients(values => [...values, client]);
            tempFavoriteClients.push(client);
        }
        let values = {
            email: user.email,
            clients: tempFavoriteClients,
            context: {
                action: 'Update Favorite Client'
            },
            endpoint: '/api/user-favorite-client/update',
            token: user.token
        };
        postRequest(values).then(response => {
            if (response.status === 200) {
                toast.success("Successfully updated favorite client", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                updateCount();
            } else {
                toast.error("An error occurred!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        });
    }

    const updateClient = (event) => {
        event.preventDefault();
        if (event.target.status.value === '') {
            toast.error("Select a status!", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
        let values = {
            dto: {
                id: selectedClient.id,
                users: selectedClient.users,
                status: statusList.find((status) => {
                    return status.statusId === Number(event.target.status.value);
                }),
            },
            context: {
                action: 'Update Client'
            },
            endpoint: '/api/client/update',
            token: user.token
        };
        postRequest(values).then(response => {
            if (response.status === 200) {
                setClients(values =>
                    values.map(client => {
                        if (client.id === selectedClient.id) {
                            return { ...response.data };
                        }
                        return client;
                    }),
                );
                setFilteredClients(values =>
                    values.map(client => {
                        if (client.id === selectedClient.id) {
                            return { ...response.data };
                        }
                        return client;
                    }),
                );
                setSelectedClient({ ...response.data });
                toast.success("Successfully updated client", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                updateCount();
            } else {
                toast.error("An error occurred!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        });
    }

    const sendReminderButton = () => {
        let values = {
            dto: selectedClients,
            context: {
                action: 'Send Reminder'
            },
            endpoint: '/api/client/send-reminder',
            token: user.token
        };
        postRequest(values).then(response => {
            if (response.status === 200) {
                toast.success(response.data.message, {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            } else {
                toast.error("An error occurred!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        });
    }

    if (!loadingContent && user) {
        const pageSize = 10,
            firstPageIndex = (currentPage - 1) * pageSize,
            lastPageIndex = firstPageIndex + pageSize,
            currentPageClients = filteredClients.slice(firstPageIndex, lastPageIndex);
        return (
            addClient ?
                <ManageClient passedClient={selectedClient} backButton={backButton} />
                : <>
                    <Header component="Favorite Clients" addClientButton={addClientButton}
                        selectedClients={selectedClients} sendReminderButton={sendReminderButton} />
                    <div className="grey-background full-height">
                        <div className="main-container manage-clients-container">
                            <div className="d-flex w-100 client-search-container">
                                {selectedClients.length > 0 &&
                                    <div className="blue-background-row mobile-send-reminder-container">
                                        <h5>{selectedClients.length} items</h5>
                                        <button className='blue-button' key="send-reminder-button"
                                            disabled={selectedClients.length === 0}
                                            onClick={() => sendReminderButton()}>
                                            Send Reminders
                                        </button>
                                    </div>
                                }
                                <div className="client-search-container-div">
                                    <input type="text" placeholder='Search Companies' className='search-input'
                                        onChange={(event) => {
                                            setFilterClientName(event.target.value);
                                            filterClients(event.target.value, filterType, selectedDate, filterBusinessType);
                                        }} />
                                    <input type="text" placeholder='Search Business Type'
                                        className='search-input search-business-type mobile-search-business-type'
                                        onChange={(event) => {
                                            setFilterBusinessType(event.target.value);
                                            filterClients(filterClientName, filterType, selectedDate, event.target.value);
                                        }} />
                                </div>

                                {!isMobile ? (
                                    <div>
                                        <div className="d-flex client-filter-container">
                                            <input type="text" placeholder='Search Business Type'
                                                className='search-input search-business-type'
                                                onChange={(event) => {
                                                    setFilterBusinessType(event.target.value);
                                                    filterClients(filterClientName, filterType, selectedDate, event.target.value);
                                                }} />
                                            <Select className="basic-single btn w-100 select"
                                                placeholder="All Status Types"
                                                name="filterType" id="filterType"
                                                options={[{
                                                    statusId: 'All Status Types',
                                                    status: 'All Status Types'
                                                }, ...statusList]}
                                                getOptionValue={option => option['statusId']}
                                                getOptionLabel={option => option['status']}
                                                onChange={(event) => {
                                                    setFilterType(values => ({
                                                        ...values,
                                                        statusId: event.statusId,
                                                        status: event.status
                                                    }));
                                                    filterClients(filterClientName, {
                                                        statusId: event.statusId,
                                                        status: event.status
                                                    }, selectedDate, filterBusinessType);
                                                }}
                                                required />

                                            <Select
                                                className="basic-single btn w-100 select mr-4"
                                                placeholder="All Clients"
                                                name="filterClientActiveStatus"
                                                id="filterClientActiveStatus"
                                                options={[
                                                    { value: "All", label: "All Clients" },
                                                    { value: "active", label: "Active Clients" },
                                                    { value: "inactive", label: "Inactive Clients" }
                                                ]}
                                                onChange={(event) => {
                                                    setFilterClientActiveStatus(event.value);
                                                    filterClients(filterClientName, filterType, selectedDate, filterBusinessType, event.value);
                                                }}
                                                required
                                            />



                                            <DatePicker
                                                placeholderText="Enter date range"
                                                selectsRange={true}
                                                startDate={selectedDate.start}
                                                endDate={selectedDate.end}
                                                onChange={(update) => {
                                                    setSelectedDate(values => ({ ...values, start: update[0], end: update[1] }));
                                                    filterClients(filterClientName, filterType, {
                                                        start: update[0],
                                                        end: update[1]
                                                    }, filterBusinessType);
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <input type="text" placeholder='Search Business Type'
                                            className='search-input search-business-type'
                                            onChange={(event) => {
                                                setFilterBusinessType(event.target.value);
                                                filterClients(filterClientName, filterType, selectedDate, event.target.value);
                                            }} />
                                        <div className="client-filter-container">
                                            <Select className="basic-single btn w-100 select"
                                                placeholder="All Status Types"
                                                name="filterType"
                                                options={[{
                                                    statusId: 'All Status Types',
                                                    status: 'All Status Types'
                                                }, ...statusList]}
                                                getOptionValue={option => option['statusId']}
                                                getOptionLabel={option => option['status']}
                                                onChange={(event) => {
                                                    setFilterType(values => ({
                                                        ...values,
                                                        statusId: event.statusId,
                                                        status: event.status
                                                    }));
                                                    filterClients(filterClientName, {
                                                        statusId: event.statusId,
                                                        status: event.status
                                                    }, selectedDate, filterBusinessType);
                                                }}
                                                required
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        fontSize: '13px'
                                                    }),
                                                    option: (provided, state) => ({
                                                        ...provided,
                                                        fontSize: '13px'
                                                    }),
                                                    singleValue: (provided) => ({
                                                        ...provided,
                                                        fontSize: '13px'
                                                    }),
                                                    placeholder: (provided) => ({
                                                        ...provided,
                                                        fontSize: '13px'
                                                    })
                                                }} />
                                            <Select
                                                className="basic-single btn w-100 select"
                                                placeholder="All Clients"
                                                name="filterClientActiveStatus"
                                                id="filterClientActiveStatus"
                                                options={[
                                                    { value: "All", label: "All Clients" },
                                                    { value: "active", label: "Active Clients" },
                                                    { value: "inactive", label: "Inactive Clients" }
                                                ]}
                                                onChange={(event) => {
                                                    setFilterClientActiveStatus(event.value);
                                                    filterClients(filterClientName, filterType, selectedDate, filterBusinessType, event.value);
                                                }}
                                                required
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        fontSize: '13px'
                                                    }),
                                                    option: (provided, state) => ({
                                                        ...provided,
                                                        fontSize: '13px'
                                                    }),
                                                    singleValue: (provided) => ({
                                                        ...provided,
                                                        fontSize: '13px'
                                                    }),
                                                    placeholder: (provided) => ({
                                                        ...provided,
                                                        fontSize: '13px'
                                                    })
                                                }}
                                            />
                                        </div>
                                        <DatePicker
                                            placeholderText="Enter date range" className="mt-2"
                                            selectsRange={true}
                                            startDate={selectedDate.start}
                                            endDate={selectedDate.end}
                                            onChange={(update) => {
                                                setSelectedDate(values => ({ ...values, start: update[0], end: update[1] }));
                                                filterClients(filterClientName, filterType, {
                                                    start: update[0],
                                                    end: update[1]
                                                }, filterBusinessType);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            <table className="w-100 client-table">
                                <tbody>
                                    {tableBody(currentPageClients)}
                                </tbody>
                            </table>
                            <Pagination
                                className="pagination-bar"
                                currentPage={currentPage}
                                totalCount={filteredClients.length}
                                pageSize={pageSize}
                                onPageChange={page => setCurrentPage(page)}
                            />
                        </div>
                        <div className="modal fade" id="updateFavoriteClientStatusModal" tabIndex="-1"
                            aria-labelledby="updateFavoriteClientStatusModalLabel"
                            aria-hidden="true">
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h1 className="modal-title fs-5 modelfile-title" id="updateFavoriteClientStatusModalLabel">Update
                                            Client
                                            Status</h1>
                                        {/* <button type="button" className="btn-close" data-bs-dismiss="modal"
                                                aria-label="Close"/> */}
                                        <button type="button" className="close-btn" data-bs-dismiss="modal" aria-label="Close">
                                            <svg width="28" height="29" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <g id="vuesax/bold/close-circle">
                                                    <g id="close-circle">
                                                        <path id="Vector" d="M13.9997 3.18311C7.57134 3.18311 2.33301 8.42144 2.33301 14.8498C2.33301 21.2781 7.57134 26.5164 13.9997 26.5164C20.428 26.5164 25.6663 21.2781 25.6663 14.8498C25.6663 8.42144 20.428 3.18311 13.9997 3.18311ZM17.9197 17.5331C18.258 17.8714 18.258 18.4314 17.9197 18.7698C17.7447 18.9448 17.523 19.0264 17.3013 19.0264C17.0797 19.0264 16.858 18.9448 16.683 18.7698L13.9997 16.0864L11.3163 18.7698C11.1413 18.9448 10.9197 19.0264 10.698 19.0264C10.4763 19.0264 10.2547 18.9448 10.0797 18.7698C9.74134 18.4314 9.74134 17.8714 10.0797 17.5331L12.763 14.8498L10.0797 12.1664C9.74134 11.8281 9.74134 11.2681 10.0797 10.9298C10.418 10.5914 10.978 10.5914 11.3163 10.9298L13.9997 13.6131L16.683 10.9298C17.0213 10.5914 17.5813 10.5914 17.9197 10.9298C18.258 11.2681 18.258 11.8281 17.9197 12.1664L15.2363 14.8498L17.9197 17.5331Z" fill="#B2B9C2" />
                                                    </g>
                                                </g>
                                            </svg>
                                        </button>
                                    </div>
                                    {selectedClient &&
                                        <form onSubmit={updateClient} className="modal-body">
                                            <p>Update status of <strong>{selectedClient.name}</strong></p>
                                            <Select className="basic-single btn w-100 select"
                                                placeholder="Status"
                                                name="status" id="status"
                                                options={statusList}
                                                getOptionValue={option => option['statusId']}
                                                getOptionLabel={option => option['status']}
                                                defaultValue={selectedClient.status}
                                                required />
                                            <div className="admin-form-submit-button-container modal-button-container">
                                                <button type="submit" className='blue-button' data-bs-dismiss="modal">
                                                    Update
                                                </button>
                                                <button type="button" className='black-button' data-bs-dismiss="modal">
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    }
                                </div>
                            </div>
                        </div>
                    </div >
                </>
        );
    } else {
        return <Loading />;
    }
}

export default FavoriteClients;
